import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { useState, type ComponentProps } from 'react';
import { Calendar } from './calendar';
import { FlexCol } from './flex';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Text } from './text';

type InputProps = ComponentProps<'input'> & {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
};

function Input({
  className,
  type,
  required,
  label,
  error,
  icon,
  value,
  onChange,
  placeholder,
  ...props
}: InputProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const renderLabel = () => {
    if (!required && !label) return null;
    return (
      <label>
        {required && (
          <Text as="span" color="danger" className="mr-0.5">
            *
          </Text>
        )}
        {label && (
          <Text as="span" variant="body-sm">
            {label}
          </Text>
        )}
      </label>
    );
  };

  const renderError = () => {
    if (!error) return null;
    return (
      <Text variant="body-xs" color="danger">
        {error}
      </Text>
    );
  };

  if (type === 'date') {
    const dateValue = value
      ? new Date((value as string) + 'T00:00:00')
      : undefined;
    const displayValue = dateValue
      ? dateValue.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';

    return (
      <FlexCol className="gap-1">
        {renderLabel()}
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-slot="input"
              className={cn(
                'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                error && 'border-red-500',
                !displayValue && 'text-muted-foreground',
                className
              )}
            >
              <span>{displayValue || placeholder || 'Select a date...'}</span>
              <CalendarIcon className="h-4 w-4 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateValue}
              onSelect={date => {
                if (onChange) {
                  const syntheticEvent = {
                    target: {
                      value: date
                        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                        : '',
                    },
                  } as React.ChangeEvent<HTMLInputElement>;
                  onChange(syntheticEvent);
                }
                setIsCalendarOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {renderError()}
      </FlexCol>
    );
  }

  return (
    <FlexCol className="gap-1">
      {renderLabel()}
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
            {icon}
          </div>
        )}
        <input
          type={type}
          data-slot="input"
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500',
            icon && 'pl-9',
            className
          )}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          {...props}
        />
      </div>
      {renderError()}
    </FlexCol>
  );
}

export { Input };
